import React, { useMemo } from 'react';
import { Share2, User } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import { useSocialPlatforms } from '../../hooks/useSocialPlatforms';
import { SocialProfilePicture } from '../common/SocialProfilePicture';
import type { SocialPlatform } from '../icons/SocialIcon';
import { useTranslation } from 'react-i18next';
import { SOCIAL_PLATFORMS, Platform } from '../../constants/SocialPlatforms';

interface SocialAccount {
  platform: SocialPlatform;
  username: string;
  picture_url?: string;
}

interface PlatformsCardProps {
  selectedPlatforms: SocialPlatform[];
  onPlatformToggle: (platform: SocialPlatform) => void;
  onFormatChange?: (format: 'regular' | 'reel') => void;
  format?: 'text' | 'reel' | 'regular';
  connectedAccounts?: SocialAccount[];
  tiktokAccount?: {
    username: string;
    picture_url?: string;
  };
}

export const PlatformsCard: React.FC<PlatformsCardProps> = ({
  selectedPlatforms,
  onPlatformToggle,
  onFormatChange,
  format = 'text',
  connectedAccounts = [],
  tiktokAccount
}) => {
  const { t } = useTranslation();
  const { availablePlatforms } = useSocialPlatforms();

  // Add tiktokAccount to connectedAccounts if it exists and not already included
  const allConnectedAccounts = React.useMemo(() => {
    const accounts = [...connectedAccounts];
    
    if (tiktokAccount?.username && !accounts.some(acc => acc.platform === 'tiktok')) {
      accounts.push({
        platform: 'tiktok',
        username: tiktokAccount.username,
        picture_url: tiktokAccount.picture_url
      });
    }
    
    return accounts;
  }, [connectedAccounts, tiktokAccount]);

  const handlePlatformToggle = (platform: SocialPlatform) => {
    onPlatformToggle(platform);
    
    // Change format to reel when selecting YouTube or TikTok
    if ((platform === 'youtube' || platform === 'tiktok') && onFormatChange) {
      if (!selectedPlatforms.includes(platform)) {
        onFormatChange('reel');
      }
    }
  };

  // Calculate platforms for the default/text format
  const defaultPlatforms = useMemo(() => {
    const platforms = [...availablePlatforms];
    const comingSoonToAdd = SOCIAL_PLATFORMS.filter(
      (p: Platform) =>
        (p.id === 'facebook' || p.id === 'instagram') &&
        p.status === 'coming-soon' &&
        !availablePlatforms.some(ap => ap.id === p.id)
    ).map(p => ({ 
        id: p.id as SocialPlatform, 
        name: p.name, 
        status: p.status,
        icon: p.icon
    }));
    
    // Add unique coming soon platforms
    for (const csPlatform of comingSoonToAdd) {
        if (!platforms.some(p => p.id === csPlatform.id)) {
            platforms.push(csPlatform);
        }
    }
    return platforms;
  }, [availablePlatforms]);

  // Filter platforms based on format
  const displayPlatforms = format === 'reel' 
    ? availablePlatforms.filter(platform => 
        ['facebook', 'instagram', 'youtube', 'tiktok', 'linkedin'].includes(platform.id)
      )
    : defaultPlatforms; // Use the calculated default list for non-reel formats

  // Find connected account info for a platform if available
  const getConnectedAccount = (platformId: SocialPlatform) => {
    return allConnectedAccounts.find(account => account.platform === platformId);
  };

  // Render platform button with appropriate display based on connected account status
  const renderPlatformButton = (platform: { id: SocialPlatform; name: string }) => {
    const isSelected = selectedPlatforms.includes(platform.id);
    const connectedAccount = getConnectedAccount(platform.id);
    
    // Special rendering for platforms with connected accounts
    if (connectedAccount) {
      return (
        <button
          key={platform.id}
          onClick={() => handlePlatformToggle(platform.id)}
          className={`flex flex-col items-center justify-center gap-1 sm:gap-2 p-1 sm:p-2 py-4 w-full h-24 rounded-lg border border-black min-w-0 max-w-full ${
            isSelected
              ? 'border-primary-600 bg-primary-50'
              : 'hover:border-primary-600'
          }`}
        >
          <SocialProfilePicture 
            pictureUrl={connectedAccount.picture_url}
            username={connectedAccount.username}
            platform={platform.id}
            size="md"
          />
          <span className="text-xs text-gray-700 w-full text-center line-clamp-2 overflow-hidden whitespace-normal break-words">@{connectedAccount.username}</span>
        </button>
      );
    }
    
    // Default rendering for platforms without connected accounts
    return (
      <button
        key={platform.id}
        onClick={() => handlePlatformToggle(platform.id)}
        className={`flex flex-col items-center justify-center gap-1 sm:gap-2 p-1 sm:p-2 py-4 w-full h-24 rounded-lg border border-black min-w-0 max-w-full ${
          isSelected
            ? 'border-primary-600 bg-primary-50'
            : 'hover:border-primary-600'
        }`}
      >
        <SocialIcon platform={platform.id} size={24} />
        <span className="text-xs text-gray-700 w-full text-center line-clamp-2 overflow-hidden whitespace-normal break-words">{platform.name}</span>
      </button>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <Share2 className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">{t('cards.platforms.title', 'Platforms')}</h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        {displayPlatforms.map(platform => renderPlatformButton(platform))}
      </div>
    </div>
  );
};